"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const axios_1 = __importDefault(require("axios"));
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const zod_1 = require("zod");
const strings_1 = require("../../../../../constants/strings");
const sessionQuery_1 = require("../../../../../queries/sessionQuery");
const routerUtils_1 = require("../../../../../router/routerUtils");
const phoneNumberUtils_1 = require("../../../../../utils/phoneNumberUtils");
const zustandStore_1 = require("../../../../../zustandStore");
const setupTwoFactorAuth_1 = require("../../activateForm/setupTwoFactorAuth");
const formWrapper_1 = __importDefault(require("../../hsbc/formWrapper"));
const useValidation_1 = require("../../hsbc/hooks/useValidation");
const allSetMessage_1 = __importDefault(require("./allSetMessage"));
const logger = (0, pino_1.default)();
const validationSchema = zod_1.z.object({
    code1: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code2: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code3: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code4: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code5: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code6: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
});
const StyledTextArea = (0, material_1.styled)(material_1.TextareaAutosize)(({ theme }) => ({
    width: '40px !important',
    height: '40px !important',
    marginRight: `${theme.tsGoUnit.tsUnitHalf} !important`,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.tsGoUnit.tsUnitHalf} !important`,
    lineHeight: '13px !important',
    background: '#FFFFFF',
    borderRadius: '2px',
    flex: 'none',
    resize: 'none',
    outline: '1px solid #CDD5DF',
    border: '4px solid transparent',
    boxSizing: 'border-box',
    fontWeight: theme.typography.tsFontWeight.bold,
    fontSize: `${theme.typography.pxToRem(22)} !important`,
    '&:last-child': {
        marginRight: '0',
    },
    '&:hover': {
        outline: '1px solid #A70262',
        border: '4px solid rgba(167, 2, 98, 0.3)',
    },
}));
const EnterValidationCode = ({ challengeName }) => {
    const theme = (0, material_1.useTheme)();
    const [formState, setFormState] = (0, react_1.useState)({
        code1: '',
        code2: '',
        code3: '',
        code4: '',
        code5: '',
        code6: '',
    });
    const { navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    const [showAllSetComponent, setShowAllSetComponent] = (0, react_1.useState)(false);
    const { validateField, clearFieldError, validateAllSchema } = (0, useValidation_1.useValidation)();
    const { steps, setPageLoading, setCurrentStep, setNotification, resetPasswordLoginState } = (0, zustandStore_1.useZustand)(state => ({
        ...state,
    }));
    const sendSmsCode = (0, sessionQuery_1.useSendSmsCodeQuery)();
    const askForSmsCode = (0, sessionQuery_1.useAskForSmsCodeQuery)();
    const sendSmsMfaCode = (0, sessionQuery_1.useSendMfaCodeChallengeQuery)();
    const fullPhoneNumber = steps.data.phoneSetup?.phoneDetails?.fullPhoneNumber || steps.data.mfaSms?.phoneDetails?.fullPhoneNumber;
    const maskedPhoneNumber = (0, phoneNumberUtils_1.maskPhoneNumber)(steps.data.phoneSetup?.phoneDetails?.phoneCountryCode || steps.data.mfaSms?.phoneDetails?.phoneCountryCode, steps.data.phoneSetup?.phoneDetails?.phoneNumber || steps.data.mfaSms?.phoneDetails?.phoneNumber);
    const inputRefs = (0, react_1.useRef)([]);
    const [errorMessage, setErrorMessage] = (0, react_1.useState)(null);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // restrict to numbers only
        if (!/^\d*$/.test(value)) {
            return;
        }
        if (value.length === 6) {
            const updatedState = { ...formState };
            const codeDigits = [...value];
            codeDigits.forEach((digit, index) => {
                updatedState[`code${index + 1}`] = digit;
            });
            setFormState(updatedState);
            clearFieldError(name);
            inputRefs.current[5]?.focus();
        }
        else if (value.length === 1) {
            setFormState(prev => {
                const updatedState = { ...prev, [name]: value };
                clearFieldError(name);
                return updatedState;
            });
            const nextIndex = Number(name.replace('code', '')) + 1;
            if (nextIndex <= 6) {
                inputRefs.current[nextIndex - 1]?.focus();
            }
        }
    };
    const handleKeyDown = (e) => {
        if (!e) {
            return;
        }
        const { name = '' } = e.target;
        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            const currentIndex = Number(name.replace('code', ''));
            const updatedState = { ...formState, [name]: '' };
            setFormState(updatedState);
            clearFieldError(name);
            if ((e.key === 'Backspace' || e.key === 'Delete') && currentIndex > 1) {
                inputRefs.current[currentIndex - 2]?.focus();
            }
        }
    };
    const handleBlur = (e) => {
        if (e) {
            const { name = '' } = e.target;
            validateField(validationSchema, name, formState);
        }
    };
    const handleMutationError = error => {
        logger.error(error);
        const errorMessage = axios_1.default.isAxiosError(error) && error?.response?.data ? error.response.data : setupTwoFactorAuth_1.invalidOtpCodeSentMessage;
        if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP && axios_1.default.isAxiosError(error)) {
            if (error?.response?.data === 'There was a problem verifying the code' || error?.response?.status === 400) {
                setErrorMessage("You've entered the wrong code, please try again.");
            }
            else if (error.response?.status === 403 || error.response?.data?.errorCode === 'LOCKED_ACCOUNT') {
                navigateToLockedAccountMessage('activation');
                return;
            }
            else {
                setNotification([{ type: strings_1.notificationStrings.ERROR, message: errorMessage }]);
            }
        }
        else {
            setNotification([{ type: strings_1.notificationStrings.ERROR, message: errorMessage }]);
        }
    };
    const handleMutationSettled = () => {
        setPageLoading(false);
    };
    const clearFormState = () => {
        setFormState({
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        });
    };
    const handleMutationSuccess = (stepIncrement, showAllSet = false) => {
        setCurrentStep(steps.currentStep + stepIncrement);
        clearFormState();
        if (showAllSet) {
            setShowAllSetComponent(true);
        }
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        setErrorMessage(null);
        validateAllSchema(validationSchema, formState);
        const code = `${formState.code1}${formState.code2}${formState.code3}${formState.code4}${formState.code5}${formState.code6}`;
        const mutationConfig = {
            onSettled: handleMutationSettled,
        };
        if (challengeName === 'SMS_MFA') {
            sendSmsMfaCode.mutate({ code, challengeName }, {
                ...mutationConfig,
                onSuccess: () => handleMutationSuccess(1, true),
                onError: handleMutationError,
            });
        }
        else {
            sendSmsCode.mutate({ code, challengeName }, {
                ...mutationConfig,
                onSuccess: async (data) => {
                    if (data.phoneSetup.status === 'verified') {
                        handleMutationSuccess(1, true);
                    }
                    else {
                        setNotification([{ type: 'error', message: setupTwoFactorAuth_1.invalidOtpCodeSentMessage }]);
                    }
                },
                onError: handleMutationError,
            });
        }
    };
    return showAllSetComponent ? ((0, jsx_runtime_1.jsx)(allSetMessage_1.default, { children: (0, jsx_runtime_1.jsxs)(formWrapper_1.default, { style: { width: 'auto', border: 'none', paddingTop: 0 }, formName: "Enter validation code", onSubmit: handleSubmit, isFormPending: sendSmsCode.isPending || askForSmsCode.isPending || sendSmsMfaCode.isPending, submitButton: {
                label: strings_1.sessionStrings.CONTINUE,
                isDisabled: !validationSchema.safeParse(formState).success,
                style: { marginTop: 0 },
            }, formFooter: challengeName === 'SMS_MFA' ? ((0, jsx_runtime_1.jsxs)(material_1.Typography, { sx: {
                    fontStyle: 'normal',
                    lineHeight: '18px',
                    color: 'black',
                    textAlign: 'center',
                }, children: ["Didn\u2019t get a text?", '  ', (0, jsx_runtime_1.jsxs)(material_1.Link, { href: "#", sx: { color: 'black', textDecorationColor: 'black' }, onClick: e => {
                            e?.preventDefault();
                            resetPasswordLoginState();
                        }, children: ["Send again", ' '] })] })) : null, children: [fullPhoneNumber ? ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: { marginBottom: '16px', fontSize: theme.typography.pxToRem(14), color: 'gray' }, children: `Enter the 6-digit code that was sent to ${maskedPhoneNumber}` })) : null, (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { display: 'flex', justifyContent: 'center', marginBottom: '16px' }, children: ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].map((code, index) => ((0, jsx_runtime_1.jsx)(StyledTextArea, { autoFocus: index === 0, name: code, value: formState[code], onChange: handleInputChange, onBlur: handleBlur, onKeyDown: handleKeyDown, maxLength: 6, minRows: 1, maxRows: 1, ref: el => (inputRefs.current[index] = el) }, code))) }), (0, jsx_runtime_1.jsxs)(material_1.Typography, { sx: { marginBottom: '16px', fontSize: '12px', color: 'gray' }, children: ["It may take a while for the code to arrive. ", (0, jsx_runtime_1.jsx)("br", {}), challengeName === 'SMS_MFA' ? 'OTP is only valid for 3 minutes.' : null] })] }) })) : ((0, jsx_runtime_1.jsxs)(formWrapper_1.default, { style: { width: 'auto', border: 'none', paddingTop: 0 }, formName: "Enter validation code", onSubmit: handleSubmit, isFormPending: sendSmsCode.isPending || askForSmsCode.isPending || sendSmsMfaCode.isPending, submitButton: {
            label: strings_1.sessionStrings.CONTINUE,
            isDisabled: !validationSchema.safeParse(formState).success,
            style: { marginTop: 0 },
        }, formFooter: challengeName === 'SMS_MFA' ? ((0, jsx_runtime_1.jsxs)(material_1.Typography, { sx: {
                fontStyle: 'normal',
                lineHeight: '18px',
                color: 'black',
                textAlign: 'center',
            }, children: ["Didn\u2019t get a text?", '  ', (0, jsx_runtime_1.jsxs)(material_1.Link, { href: "#", sx: { color: 'black', textDecorationColor: 'black' }, onClick: e => {
                        e?.preventDefault();
                        resetPasswordLoginState();
                    }, children: ["Send again", ' '] })] })) : null, children: [fullPhoneNumber ? ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: { marginBottom: '16px', fontSize: theme.typography.pxToRem(14), color: 'gray' }, children: `Enter the 6-digit code that was sent to ${maskedPhoneNumber}` })) : null, (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { display: 'flex', justifyContent: 'center', marginBottom: '16px' }, children: ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].map((code, index) => ((0, jsx_runtime_1.jsx)(StyledTextArea, { autoFocus: index === 0, name: code, value: formState[code], onChange: handleInputChange, onBlur: handleBlur, onKeyDown: handleKeyDown, maxLength: 6, minRows: 1, maxRows: 1, ref: el => (inputRefs.current[index] = el) }, code))) }), errorMessage && ((0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body2", sx: {
                    color: theme => theme.palette.error.main,
                    textAlign: 'center',
                    mt: 1,
                    mb: 2,
                }, children: errorMessage })), (0, jsx_runtime_1.jsxs)(material_1.Typography, { sx: { marginBottom: '16px', fontSize: '12px', color: 'gray' }, children: ["It may take a while for the code to arrive. ", (0, jsx_runtime_1.jsx)("br", {}), challengeName === 'SMS_MFA' ? 'OTP is only valid for 3 minutes.' : null] })] }));
};
exports.default = EnterValidationCode;
